import type { EntityFieldProvenance, EntityKind, EntitySource } from './entityTypes';

export function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

export function isNonEmptyArray<T = unknown>(v: unknown): v is T[] {
  return Array.isArray(v) && v.length > 0;
}

export function pickFirstNonEmpty<T>(...vals: T[]): T | null {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'string') {
      if (v.trim().length > 0) return v;
      continue;
    }
    if (Array.isArray(v)) {
      if (v.length > 0) return v;
      continue;
    }
    return v;
  }
  return null;
}

// Last.fm placeholder silhouette (we never want to show this in Maestro).
export function isLastFmPlaceholderUrl(url: unknown): boolean {
  const u = String(url || '').trim();
  if (!u) return false;
  return u.includes('2a96cbd8b46e442fc41c2b86b821562f');
}

// Higher number wins when deciding which source can replace an existing value.
export function sourcePriority(source: EntitySource): number {
  switch (source) {
    case 'enriched':
      return 50;
    case 'db':
      return 40;
    case 'freshness':
      return 35;
    case 'mb':
      return 30;
    case 'baton':
      return 20;
    case 'preview':
      return 10;
    case 'local':
      return 5;
    case 'loading':
    default:
      return 0;
  }
}

export function shouldReplaceField(
  prevValue: unknown,
  nextValue: unknown,
  prevMeta: EntityFieldProvenance | undefined,
  nextSource: EntitySource
): boolean {
  // Monotonic rule: never regress to empty/null.
  const nextEmpty =
    nextValue === null ||
    nextValue === undefined ||
    (typeof nextValue === 'string' && nextValue.trim().length === 0) ||
    (Array.isArray(nextValue) && nextValue.length === 0);
  if (nextEmpty) return false;

  const prevEmpty =
    prevValue === null ||
    prevValue === undefined ||
    (typeof prevValue === 'string' && prevValue.trim().length === 0) ||
    (Array.isArray(prevValue) && prevValue.length === 0);
  if (prevEmpty) return true;

  const prevPrio = prevMeta ? sourcePriority(prevMeta.source) : 0;
  const nextPrio = sourcePriority(nextSource);

  // Prefer higher-precedence sources; if equal, keep existing to avoid flicker.
  if (nextPrio > prevPrio) return true;

  // Allow same-source updates (e.g., db fixes) to refresh stale fields deterministically.
  // This fixes cases where a wrong value was previously stored under the same source.
  if (prevMeta && prevMeta.source === nextSource) {
    // Only update if the incoming value is actually different.
    const changed = JSON.stringify(prevValue) !== JSON.stringify(nextValue);
    return changed;
  }

  return false;
}

export function normalizeKindFromArtistLike(input: any, hint?: EntityKind): EntityKind {
  if (hint && hint !== 'unknown') return hint;
  const t = String(input?.type || input?.kind || '').toLowerCase();
  const genres = Array.isArray(input?.genres) ? input.genres.map((g: any) => String(g).toLowerCase()) : [];
  const roles = Array.isArray(input?.primaryRoles || input?.primary_roles)
    ? (input.primaryRoles || input.primary_roles).map((r: any) => String(r).toLowerCase())
    : [];

  if (t.includes('group') || t.includes('band')) return 'group';
  if (t.includes('composer') || genres.some((g) => g.includes('composer')) || roles.some((r) => r.includes('composer')))
    return 'composer';
  if (t.includes('producer') || roles.some((r) => r.includes('producer'))) return 'producer';
  if (t.includes('person')) return 'person';
  return 'unknown';
}

